import axios from "axios";
import { getApiBaseUrl } from "../utils/urlHandler";

// contact_ingest's routes (SGL-80/81/82) live on the same AIAgentApi Gateway
// as /ai-agents, just under /{customer_id}/contacts/... — there's no separate
// env var for it, and adding one means another Amplify build var to keep in
// sync across dev/prod (see app-daisy-ai/CLAUDE.md on that trap), so derive
// the stage root from the agent API URL that's already configured everywhere.
const AGENT_API_URL = getApiBaseUrl("REACT_APP_AGENT_API_URL");
const API_ROOT = AGENT_API_URL.replace(/\/ai-agents\/?$/, "");

const CONTACT_EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const CONTACT_PHONE_RE = /(?:\+?\d[\d\s().-]{7,}\d)/;

/** Loose "is this CSV" check — a comma-separated first line is enough of a
 *  signal to route it as `csv` rather than `text`; the backend's own
 *  csv_parse falls through to the LLM extraction path anyway if it turns
 *  out not to really be CSV-shaped. */
export const looksLikeCsv = (raw: string): boolean => {
  const firstLine = raw.split(/\r?\n/, 1)[0] ?? "";
  return firstLine.includes(",") && firstLine.split(",").length >= 2;
};

/** Heuristic for "the user pasted a contact list into the main composer"
 *  rather than asking a normal question — requires multiple lines, most of
 *  which look like they carry an email or phone number. Deliberately
 *  conservative (>=2 matching lines, >=50% of all lines) so a one-off email
 *  mention in a sentence doesn't misfire and get routed away from chat. */
export const looksLikeContactDump = (raw: string): boolean => {
  const lines = raw.trim().split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return false;
  const matching = lines.filter((l) => CONTACT_EMAIL_RE.test(l) || CONTACT_PHONE_RE.test(l));
  return matching.length >= 2 && matching.length / lines.length >= 0.5;
};

/** Short allowlist of "list/show my contacts" phrasings — deliberately a
 *  fixed phrase match, not free-form NLP (that would need a real chat-service
 *  agent tool, a separate and much bigger build). A short, typed chat message
 *  matching one of these is unambiguous enough to route without a "+" menu click. */
const LIST_CONTACTS_PHRASES = [
  // (me\s+)? before (all\s+)? — "show me my contacts" and "show me all my
  // contacts" both need to match; a bare "show my contacts" still matches
  // since both groups are optional. Verb list widened 2026-08-26 after
  // "give me my contacts" fell through — "list/show/see/view" alone missed
  // any request phrased as an ask rather than a command.
  /\b(list|show|see|view|give|get|pull\s+up|fetch)\s+(me\s+)?(all\s+)?my\s+contacts\b/i,
  /\bwhat\s+contacts\s+do\s+i\s+have\b/i,
  /\ball\s+my\s+contacts\b/i,
];

export const looksLikeContactsListRequest = (raw: string): boolean => {
  const text = raw.trim();
  if (!text || text.length > 120) return false; // a real question, not a short command
  return LIST_CONTACTS_PHRASES.some((re) => re.test(text));
};

export type ContactIssue = "no_email_or_phone" | "invalid_email" | "invalid_phone";
export type ContactStatus = "ok" | "needs_review" | "duplicate_in_batch" | "already_in_crm";

export interface ImportedContact {
  name: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  notes: string;
  in_crm: boolean | null;
  ghl_contact_id: string | null;
  issues: ContactIssue[];
  status: ContactStatus;
  /** Indices of other raw rows merged into this one (primary rows only). */
  merged_from?: number[];
  /** Set once POST /contacts/upsert has pushed this contact to GHL. */
  upserted?: boolean;
  email_eligible?: boolean;
  /** UpsertResult.error from the last failed /contacts/upsert attempt for this
   *  row (e.g. "ghl_status_422") — null/undefined once it succeeds. A per-contact
   *  failure doesn't touch `status`/`issues` (those describe parsing, not the
   *  write), so this is the only signal the review UI has to badge it. */
  upsert_error?: string | null;
}

export interface ContactBatchSummary {
  raw_rows: number;
  parsed: number;
  merged_duplicates: number;
  already_in_crm: number;
  needs_review: number;
  ready: number;
  /** "ok" | "not_connected" | "needs_reauth" | "unavailable" — read-only cross-check outcome. */
  ghl_check: string | null;
}

export interface ContactBatch {
  batch_id: string;
  contacts: ImportedContact[];
  summary: ContactBatchSummary;
  /** Best-effort Bedrock suggestion for the import tag (SGL-81) — null when
   *  the suggestion call failed. Always just a starting point: the review
   *  UI pre-fills it as a removable/editable chip, never applies it silently. */
  suggested_tag: string | null;
}

export type PatchableContactFields = Partial<
  Pick<ImportedContact, "name" | "company" | "email" | "phone" | "website" | "notes">
>;

export interface UpsertResult {
  index: number;
  ok: boolean;
  ghl_contact_id?: string | null;
  is_new?: boolean;
  email_eligible?: boolean;
  error?: string | null;
}

export interface UpsertResponse {
  batch_id: string;
  results: UpsertResult[];
  summary: { requested: number; succeeded: number; failed: number };
}

const authHeaders = (idToken: string) => ({
  headers: { Authorization: `Bearer ${idToken}` },
});

/** POST /{customer_id}/contacts/import — parse a pasted or CSV contact dump. */
export const importContacts = async (
  customerId: string,
  input: { text: string } | { csv: string },
  idToken: string
): Promise<ContactBatch> => {
  const { data } = await axios.post(
    `${API_ROOT}/${customerId}/contacts/import`,
    input,
    authHeaders(idToken)
  );
  return data;
};

/** GET /{customer_id}/contacts/import/{batch_id} — re-read a persisted batch. */
export const getContactBatch = async (
  customerId: string,
  batchId: string,
  idToken: string
): Promise<ContactBatch> => {
  const { data } = await axios.get(
    `${API_ROOT}/${customerId}/contacts/import/${batchId}`,
    authHeaders(idToken)
  );
  return data;
};

/** PATCH /{customer_id}/contacts/import/{batch_id} — fix a typo before import (SGL-81). */
export const patchContact = async (
  customerId: string,
  batchId: string,
  index: number,
  fields: PatchableContactFields,
  idToken: string
): Promise<ContactBatch> => {
  const { data } = await axios.patch(
    `${API_ROOT}/${customerId}/contacts/import/${batchId}`,
    { index, fields },
    authHeaders(idToken)
  );
  return data;
};

/** POST /{customer_id}/contacts/upsert — the actual GHL write. Fails loudly (no partial-degrade). */
export const upsertContacts = async (
  customerId: string,
  batchId: string,
  contactIndices: number[],
  tags: string[],
  idToken: string
): Promise<UpsertResponse> => {
  const { data } = await axios.post(
    `${API_ROOT}/${customerId}/contacts/upsert`,
    { batch_id: batchId, contact_indices: contactIndices, tags },
    authHeaders(idToken)
  );
  return data;
};

/** One contact as it exists in GHL right now — a plain listing row (SGL-81
 *  "ask my contacts"), not an import-batch row: no issues/status/merged_from,
 *  those only make sense for a batch pending review. */
export interface GhlContact {
  ghl_contact_id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  tags: string[];
}

/** GHL's own pagination cursor: [timestamp, contact_id]. Opaque — always pass
 *  back exactly what a previous response returned, never construct one. */
export type GhlSearchCursor = [number, string];

export interface ContactsListPage {
  contacts: GhlContact[];
  total: number;
  next_search_after: GhlSearchCursor | null;
}

/** GET /{customer_id}/contacts — live listing from GHL, not tied to any
 *  import batch. Fails loudly like upsert (nothing useful to show without GHL). */
export const listContacts = async (
  customerId: string,
  idToken: string,
  options: { limit?: number; searchAfter?: GhlSearchCursor; query?: string } = {}
): Promise<ContactsListPage> => {
  const params: Record<string, string> = {};
  if (options.limit) params.limit = String(options.limit);
  if (options.searchAfter) params.search_after = JSON.stringify(options.searchAfter);
  if (options.query) params.query = options.query;
  const { data } = await axios.get(`${API_ROOT}/${customerId}/contacts`, {
    ...authHeaders(idToken),
    params,
  });
  return data;
};
